import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  REPORT_COLUMN_ORDER,
  type ContactsFunnelResponse, type ReportColumnKey, type ReportGroupBy, type ReportKpiResponse, type ReportMetricGroup,
  type ReportMetricKey, type ReportOverviewResponse, type ReportTrendResponse,
} from '@repo/types'
import { api } from '@/lib/api'
import { TENANT_INFO_KEY, useTenantInfo, type TenantInfo } from './tenant'

/** Obdobie a pobočka Reportov. `branchId` posiela len OWNER/SUPER_ADMIN, BRANCH_MANAGER-ovi ju vynúti API. */
export interface ReportScopeParams {
  from: string
  to: string
  branchId?: string
}

/** Spoločné voľby hookov; `enabled: false` odloží dotaz (napr. kým sa neoverí pobočka z URL). */
export interface ReportQueryOptions {
  enabled?: boolean
}

export interface ReportTrendParams extends ReportScopeParams {
  metric: ReportMetricKey
  groupBy: ReportGroupBy
  ids?: string[]
  byDim?: boolean
}

export const reportKeys = {
  all: ['reports'] as const,
  kpi: (p: ReportScopeParams & { groupBy: ReportGroupBy }) => ['reports', 'kpi', p] as const,
  trend: (p: ReportTrendParams) => ['reports', 'trend', p] as const,
  funnel: (p: ReportScopeParams & { agentId?: string }) => ['reports', 'contacts-funnel', p] as const,
  overview: (branchId?: string) => ['reports', 'overview', branchId ?? null] as const,
}

/**
 * Query string bez prázdnych hodnôt; pole sa spojí čiarkou. Volajúci posiela len explicitné polia:
 * API má `forbidNonWhitelisted`, takže cudzí parameter by skončil chybou 400.
 */
function toQuery(params: Record<string, string | boolean | string[] | undefined>): string {
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === '' || value === false) continue
    if (Array.isArray(value)) {
      if (value.length) search.set(key, value.join(','))
    } else {
      search.set(key, String(value))
    }
  }
  return search.toString()
}

export function useReportKpi(params: ReportScopeParams & { groupBy: ReportGroupBy }, options?: ReportQueryOptions) {
  return useQuery<ReportKpiResponse>({
    queryKey: reportKeys.kpi(params),
    enabled: options?.enabled ?? true,
    queryFn: async () => {
      const { from, to, branchId, groupBy } = params
      return (await api.get(`/reports/kpi?${toQuery({ from, to, branchId, groupBy })}`)).data
    },
    // Pri zmene filtra ostane stará tabuľka, kým nepríde nová (žiadne preblikávanie).
    placeholderData: (previous) => previous,
  })
}

export function useReportTrends(list: ReportTrendParams[], options?: ReportQueryOptions) {
  return useQueries({
    queries: list.map((params) => ({
      queryKey: reportKeys.trend(params),
      enabled: options?.enabled ?? true,
      queryFn: async (): Promise<ReportTrendResponse> => {
        const { from, to, branchId, groupBy, metric, ids, byDim } = params
        return (await api.get(`/reports/kpi/trend?${toQuery({ from, to, branchId, groupBy, metric, ids, byDim })}`)).data
      },
      // Pri zmene filtra ostane starý graf, kým nepríde nový (rovnako ako tabuľka).
      placeholderData: (previous: ReportTrendResponse | undefined) => previous,
    })),
  })
}

export function useContactsFunnel(params: ReportScopeParams & { agentId?: string }, options?: ReportQueryOptions) {
  return useQuery<ContactsFunnelResponse>({
    queryKey: reportKeys.funnel(params),
    enabled: options?.enabled ?? true,
    queryFn: async () => {
      const { from, to, branchId, agentId } = params
      return (await api.get(`/reports/contacts-funnel?${toQuery({ from, to, branchId, agentId })}`)).data
    },
    // Pri zmene filtra ostane starý lievik, kým nepríde nový (rovnako ako tabuľka a grafy).
    placeholderData: (previous) => previous,
  })
}

export function useReportOverview(branchId?: string, options?: ReportQueryOptions) {
  return useQuery<ReportOverviewResponse>({
    queryKey: reportKeys.overview(branchId),
    enabled: options?.enabled ?? true,
    queryFn: async () => (await api.get(`/reports/overview?${toQuery({ branchId })}`)).data,
    // Pri zmene pobočky ostane starý stav, kým nepríde nový (rovnako ako tabuľka a grafy).
    placeholderData: (previous) => previous,
  })
}

/** Skryté stĺpce skupiny z nastavení tenanta; poškodená hodnota (nie pole textov) = nič skryté. */
function hiddenReportColumns(tenant: TenantInfo | undefined, group: ReportMetricGroup): string[] {
  const value: unknown = tenant?.settings?.reportHiddenColumns?.[group]
  return Array.isArray(value) ? value.filter((key): key is string => typeof key === 'string') : []
}

const REPORT_COLUMNS_MUTATION_KEY = ['tenant-settings', 'report-columns'] as const

/**
 * Viditeľné stĺpce tabuľky Aktivita/Výsledky. Ukladajú sa skryté stĺpce
 * (tenant.settings.reportHiddenColumns), takže nový stĺpec sa zobrazí aj tým, ktorí si výber už uložili.
 * Výber je spoločný pre kanceláriu (rovnako ako kedysi stĺpce Môj tím); PATCH /settings smie BRANCH_MANAGER+.
 * Posiela sa len zmenená skupina, API ju zlúči s ostatnými (uloženie Aktivity neprepíše Výsledky).
 */
export function useReportColumns(group: ReportMetricGroup) {
  const qc = useQueryClient()
  const { data: tenant, isPending } = useTenantInfo()
  const all = REPORT_COLUMN_ORDER[group]
  const hidden = hiddenReportColumns(tenant, group)
  const saved = all.filter((key) => !hidden.includes(key))
  // Aspoň jeden stĺpec ostane vždy (výber, ktorý by skryl všetko, sa ignoruje).
  const columns = saved.length > 0 ? saved : all

  const save = useMutation({
    mutationKey: REPORT_COLUMNS_MUTATION_KEY,
    // Uloženia nastavení idú na server po jednom v poradí kliknutí.
    scope: { id: 'tenant-settings' },
    mutationFn: (nextHidden: string[]) => api.patch('/settings', { settings: { reportHiddenColumns: { [group]: nextHidden } } }),
    // Optimisticky do cache tenant-info: výber sa prejaví hneď a prežije aj zmenu tabu.
    onMutate: async (nextHidden) => {
      await qc.cancelQueries({ queryKey: TENANT_INFO_KEY })
      const previous = qc.getQueryData<TenantInfo>(TENANT_INFO_KEY)
      if (previous) {
        qc.setQueryData<TenantInfo>(TENANT_INFO_KEY, {
          ...previous,
          settings: {
            ...previous.settings,
            reportHiddenColumns: { ...previous.settings?.reportHiddenColumns, [group]: nextHidden },
          },
        })
      }
      return { previous }
    },
    onError: (_error, _nextHidden, context) => {
      if (context?.previous) qc.setQueryData(TENANT_INFO_KEY, context.previous)
    },
    // Obnoví sa až po poslednom uložení v rade, inak by refetch na chvíľu vrátil starší výber.
    onSettled: () => {
      if (qc.isMutating({ mutationKey: REPORT_COLUMNS_MUTATION_KEY }) === 1) {
        return qc.invalidateQueries({ queryKey: ['tenant-settings'] })
      }
    },
  })

  return {
    all,
    columns,
    /** Nastavenia tenanta sa ešte načítavajú: výber by bol len predvolený (a uloženie by prepísalo uložený). */
    loading: isPending,
    setColumns: (visible: ReportColumnKey[]) => save.mutate(all.filter((key) => !visible.includes(key))),
  }
}
